// Catering Order Related Enums

import { RecordStatus } from 'aws-sdk/clients/servicecatalog';
import { ICateringInvoice, InvoicePaymentType } from './cateringInvoice.types';
import { CateringItemCategory, ICateringOrderItem } from './cateringOrderItem.types';
import { ICateringOrderServiceCharge, ICateringOrderTax } from './cateringOrderTax.enum';
import { ICateringPackage } from './cateringPackage.types';

// Service Type Enum
export enum ServiceType {
  OFF_PREMISE = 'Off-premise',
  ON_PREMISE = 'On-premise',
}

export enum ServiceTypeV2 {
  OFF_PREMISE = 'Off premises',
  ON_PREMISE = 'On premises',
}

// Event Type Enum
export enum EventType {
  CORPORATE = 'Corporate',
  BIRTHDAY = 'Birthday',
  WEDDING = 'Wedding',
  ENGAGEMENT = 'Engagement',
  BABY_SHOWER = 'Baby shower',
  BRIDAL_SHOWER = 'Bridal shower',
  GRADUATION_PARTY = 'Graduation party',
  RELIGIOUS = 'Religious',
  HOLIDAY = 'Holiday',
  OTHER = 'Other',
}

// Order Status Enum
export enum CateringOrderStatus {
  PENDING = 'Pending',
  INVOICE = 'Invoice',
  CONFIRMED = 'Confirmed',
  REJECTED = 'Rejected',
  DEPOSIT_PAID = 'Deposit paid',
  IN_PREP = 'In prep',
  KITCHEN = 'Kitchen',
  READY = 'Ready',
  DELIVERED = 'Delivered',
  CANCELLED = 'Cancelled',
  DONE = 'Done',
}

export enum CateringOrderStatusV2 {
  PENDING_INQUIRIES = 'Pending Inquiries',
  BILLING_AND_INVOICE = 'Billing & Invoice',
  PAYMENT = 'Payment',
  REJECTED = 'Rejected',
  IN_KITCHEN = 'In Kitchen',
  READY = 'Ready',
  COMPLETED = 'Completed',
}

export enum CateringOrderSubStatus {
  PENDING_CONFIRMATION = 'Pending Confirmation',
  INVOICE_PENDING = 'Invoice Pending',
  INVOICE_DUE = 'Invoice Due',
  DEPOSIT_PAID = 'Deposit Paid',
  PAID = 'Paid',
  SCHEDULED_TO_KDS = 'Scheduled To KDS',
  SENT_TO_KDS = 'Sent To KDS',
  REJECTED = 'Rejected',
  CANCELLED = 'Cancelled',
  COMPLETED = 'Completed',
  READY = 'Ready',
}

export enum CateringOrderAuditOperationType {
  ADDED = 'ADDED',
  REMOVED = 'REMOVED',
  MODIFIED = 'MODIFIED',
}

export enum OrderStatusAction {
  ACCEPT = 'accept',
  REJECT = 'reject',
  CANCEL = 'cancel',
  MARK_READY = 'ready',
  MARK_COMPLETE = 'complete',
}

export enum CateringPaymentStatus {
  PENDING = 'Pending',
  PARTIAL_PAID = 'Partial paid',
  PAID = 'Paid',
  OVERDUE = 'Overdue',
}

export enum OrderNotificationType {
  INQUIRY_RECEIVED = 'INQUIRY_RECEIVED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  UPDATED = 'UPDATED',
  COMPLETED = 'COMPLETED',
}

export interface ICustomerInfo {
  name: string;
  email: string;
  phone: string;
  address: string;
}

export interface ICateringOrder {
  id: string;
  customerId?: string;
  restaurantId: number;
  packageId: number;
  packageInfo: ICateringPackage;
  isCustomPackage?: boolean;
  customerInfo: ICustomerInfo;
  serviceType: ServiceType;
  eventType: EventType;
  headCount: number;
  location: string;
  eventDate: string | Date;
  eventTime: string;
  specialRequests?: string;
  customPackage?: string;
  status: RecordStatus;
  orderStatus: CateringOrderStatus;
  createdAt: Date;
  updatedAt: Date;
  package?: ICateringPackage;
  items: ICateringOrderItem[];
  taxes: ICateringOrderTax[];
  serviceCharges: ICateringOrderServiceCharge[];
  invoices: ICateringInvoice[];
  source: CateringOrderSource;
  dueDate?: string | Date;
  paidAmount?: number;
  totalAmount?: number;
}

/**
 * Matches CtrOrderEntity fields (including relations as optional)
 * Use this for DTOs / service returns / typing responses.
 */
export interface ICtrOrder {
  // Columns
  id: number;
  slug: string;
  restaurantId: number;
  customerId: string | null;
  ctrPkgId: number | null;
  ctrPkgVersionId: number | null;

  serviceType: ServiceTypeV2;
  eventType: EventType;

  headCount: number | null;
  eventLocation: string;
  eventDate: Date;
  eventTime: string;

  specialRequest: string | null;
  customerInfo: any | null;

  orderStatus: CateringOrderStatusV2;
  orderSubStatus: string;
  paymentStatus: CateringPaymentStatus;

  source: string;
  reason: string | null;

  totalAmount: number;
  totalTax: number | null;
  totalServiceCharge: number | null;
  dueAmount: number | null;
  paidAmount: number | null;

  ctrPaymentPlanId: number | null;
  ctrPaymentPlanVersionId: number | null;

  status: RecordStatus;

  // AuditableEntity (common fields)
  createdAt?: Date;
  updatedAt?: Date;
  cognitoCreatedBy?: string | null;
  cognitoUpdatedBy?: string | null;

  // Relations (optional because you may not load them in queries)
  ctrPackage?: any; // CtrPackageEntity
  ctrPackageVersion?: any; // CtrPackageVersionEntity
  restaurant?: any; // Restaurant
  customer?: any; // Customer

  taxes?: any[]; // CtrOrderTaxEntity[]
  serviceCharges?: any[]; // CtrOrderServiceChargeEntity[]
  todoTasks?: any[]; // CtrOrderTodoTaskEntity[]
  ctrOrderItems?: any[]; // CtrOrderItemEntity[]
  kdsConfigs?: any[]; // CtrKdsConfigEntity[]
  ctrInvoices?: any[]; // CtrInvoiceEntity[]
}

export interface ICreateOrderValues {
  serviceType: string;
  eventType: string;
  headCount: number;
  location: string;
  eventDate: string;
  eventTime: string;
  packageId: number | null;
  packageInfo?: ICateringPackage | null;
  customerInfo: {
    name: string;
    email: string;
    phone: string;
    address: string;
  };
  lineItems: {
    id?: number | null;
    packageId?: number | null;
    description: string;
    quantity: string;
    unitPrice: string;
    category: CateringItemCategory;
  }[];
  taxes: ICateringOrderTax[];
  serviceCharges: ICateringOrderServiceCharge[];
  isInvoice: boolean;
  invoice: {
    paymentType: InvoicePaymentType;
    value: number | null;
    paymentAmount: number | null;
  };
  specialRequests: string;
  source: CateringOrderSource;
  dueDate: string;
  isCustomPackage?: boolean;
  customPackage?: string;
  budget?: string;
}

export enum CateringOrderSource {
  WEB_SITE = 'WEBSITE',
  DASH = 'DASHBOARD',
}

export type CateringOrderCount = { [key in CateringOrderStatus]: number };

export interface ICateringDashboardOrderStats extends CateringOrderCount {
  total: number;
}

export interface ICateringDashboardStaus {
  totalOrders?: number | string | null;
  totalRevenue?: number | string | null;
  totalInquiries?: number | string | null;
  completedOrders?: number | string | null;
  statsFeedback?: any;
}

// Interface for audit log response
interface AuditLogChangeItem {
  recordId: number;
  fieldName: string;
  oldValue: any;
  newValue: any;
  changeInValue: any;
  changedAt: Date;
  itemName?: string;
}

interface PackageInfo {
  id: number | null;
  name: string;
  description: string | null;
}

export interface OrderAuditLogResponse {
  menuChanges: {
    package: PackageInfo;
    added: AuditLogChangeItem[];
    removed: AuditLogChangeItem[];
    updated: AuditLogChangeItem[];
  };
  orderChanges: Omit<AuditLogChangeItem, 'recordId'>[];
}
